import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useElementItemStore } from 'components/WorkspaceEditor/ElementItemStore'
import {
    useWorkspace2Actions,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useProjectContentItemElementsMutation } from 'components/WorkspaceEditor/api/mutations/projectContentItemElements'
import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import { useTrackChangesQuery } from 'components/WorkspaceEditor/components/TrackChangesPanel/api'
import { useTreeMutationsWithTrackChanges } from 'components/WorkspaceEditor/components/TrackChangesPanel/services'
import { useAutoExpandNewArticle } from 'components/WorkspaceEditor/features/createNewContent'
import {
    useNodeMapActions,
    useNodeMapShallowSelector,
} from 'components/WorkspaceEditor/features/keyOperations'
import { useContentItemViewNode } from 'components/WorkspaceEditor/hooks/useContentItemViewNodeService'
import { useTransactionManager } from 'components/WorkspaceEditor/services/transactionManager'
import {
    ContentItemViewNodesType,
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from 'components/WorkspaceEditor/types'
import { PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback, useRef } from 'react'
import {
    getRootArticleContentItemIds,
    isActiveTabConsistent,
    isStaleResponse,
    processNonSiblingElementsOnContentMutation,
} from '../../../../utils/contentItemViewNodesUtils'
import { getSearchableElementId } from '../../../../utils/workspaceEditorUtils'

const { Enter } = PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING
interface UsePlainTextContentMutationProps {
    setUnsavedData?: (value: string) => void
    setShouldTriggerNewContent?: (value: boolean) => void
}

/**
 * Custom hook to handle plain text content mutation.
 * It provides methods to add new content and manage optimistic updates.
 *
 * @param {UsePlainTextContentMutationProps} props - Props for the hook.
 * @returns {Object} An object containing methods for handling content mutation.
 */
export const usePlainTextContentMutation = ({
    setUnsavedData,
    setShouldTriggerNewContent,
}: UsePlainTextContentMutationProps = {}) => {
    const { onContentViewFetchSuccess } = useProductFetcherUtils({
        overrideContentItemNodes: false,
    })

    const { executeWithOptimistic } = useTransactionManager()
    const { setTextContentElementToAdd } = useWorkspace2Actions()
    const {
        nodeToAdd,
        addNewArticle,
        addNewParagraph,
        contentItemViewNodes,
        previousContentItemViewNodes,
    } = useContentItemViewNode()
    const isNodeToAddArticle = nodeToAdd?.type === 'Article'
    const { addProjectContentItem } = useProjectContentItemElementsMutation()
    const { updateTreeViewNodes } = useTreeMutationsWithTrackChanges()
    const {
        setSaveOperationInProgress,
        markElementAsBeingAdded,
        markElementAsAdded,
    } = useNodeMapActions()
    const { saveOperationKey } = useNodeMapShallowSelector((state) => ({
        saveOperationKey: state.saveOperationKey,
    }))

    // Pre-calculate if this is an Enter operation to avoid runtime comparison
    const isEnterOperation = saveOperationKey === Enter
    // Utility to reset operation states on errors
    const resetOperationStates = useCallback(() => {
        setShouldTriggerNewContent?.(false)
        setSaveOperationInProgress(false)
        setTextContentElementToAdd(null)
    }, [
        setShouldTriggerNewContent,
        setSaveOperationInProgress,
        setTextContentElementToAdd,
    ])

    // Use a ref to always have access to the latest value
    const latestContentItemViewNodesRef = useRef<
        Partial<ContentItemViewNodesType>[] | null
    >(null)

    // Hook for managing auto-expansion of newly created articles
    const {
        prepareForAutoExpand,
        handleAutoExpandAfterSave,
        cleanupAutoExpand,
    } = useAutoExpandNewArticle()

    // Ref to track activeTab when save operation started (for race condition prevention)
    const initialActiveTabRef = useRef<number | null>(null)

    const { fetchTrackChangesDataWithApolloClient } = useTrackChangesQuery()

    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            try {
                // Get currently selected elementId (family) and activeTab from store
                const currentElementId = useElementItemStore.getState()
                    .elementItemValue.elementId
                const currentActiveTab = useWorkspace2Store.getState().activeTab

                // Check if response is stale ignoring optimistic callback when elementIds are not consistent
                if (isStaleResponse(response, currentElementId)) {
                    cleanupAutoExpand()
                    setSaveOperationInProgress(false)

                    return
                }

                // Check if activeTab changed during save operation (tab switch race condition)
                if (
                    !isActiveTabConsistent(
                        initialActiveTabRef.current,
                        currentActiveTab
                    )
                ) {
                    cleanupAutoExpand()
                    fetchTrackChangesDataWithApolloClient()
                    setSaveOperationInProgress(false)

                    return // Ignoring callback - tab switched
                }

                const optimisticTreeViewNodes = await onContentViewFetchSuccess(
                    response,
                    [],
                    true,
                    latestContentItemViewNodesRef.current
                )

                if (
                    optimisticTreeViewNodes &&
                    optimisticTreeViewNodes.length > 0
                ) {
                    // Auto-expand newly added article if applicable
                    handleAutoExpandAfterSave(optimisticTreeViewNodes)

                    updateTreeViewNodes(optimisticTreeViewNodes)
                    setShouldTriggerNewContent?.(isEnterOperation)
                }

                // Always reset save operation flag after callback completes
                setSaveOperationInProgress(false)
            } catch (error) {
                // silently catch only to prevent blocking user flow
                cleanupAutoExpand()
                resetOperationStates()
            }
        },
        onError: () => {
            cleanupAutoExpand()
            resetOperationStates()
        },
    })

    const handleContentMutationCompleted = useCallback(
        async (
            textElement: TextContentElement,
            isArticle = false,
            shouldSetTextContentElementToAddNull = false,
            blurStartActiveTab?: number
        ) => {
            try {
                let rootArticleContentItemIds: string[] | null = null
                initialActiveTabRef.current =
                    blurStartActiveTab ??
                    useWorkspace2Store.getState().activeTab

                if (!isArticle) {
                    // Strip the _additional suffix if it exists for text between pills
                    const elementIdForSearch = getSearchableElementId(
                        textElement.id
                    )

                    rootArticleContentItemIds = getRootArticleContentItemIds(
                        contentItemViewNodes,
                        [elementIdForSearch]
                    )
                }
                if (shouldSetTextContentElementToAddNull) {
                    const {
                        updatedContentItem,
                    } = processNonSiblingElementsOnContentMutation(
                        contentItemViewNodes,
                        [textElement.id]
                    )
                    latestContentItemViewNodesRef.current = updatedContentItem
                    setTextContentElementToAdd(null)
                } else {
                    latestContentItemViewNodesRef.current = null
                }

                if (initialActiveTabRef.current === null) {
                    initialActiveTabRef.current = useWorkspace2Store.getState().activeTab
                }

                await getContentItemViewNodesWithApolloClient(
                    rootArticleContentItemIds
                )
            } catch (error) {
                // silently catch only to prevent blocking user flow
            }
        },
        [
            contentItemViewNodes,
            setTextContentElementToAdd,
            getContentItemViewNodesWithApolloClient,
            updateTreeViewNodes,
            processNonSiblingElementsOnContentMutation,
        ]
    )

    const handleAddArticleContentExecuteOptimisticWithFallback = useCallback(
        async (
            fallbackState: string,
            textElement: TextContentElement,
            blurStartActiveTab?: number
        ) => {
            const variables = addNewArticle(fallbackState) || undefined

            if (!variables) {
                throw new Error(`Unsupported Type: ${nodeToAdd?.type}`)
            }

            // Element should already be marked as being added in the blur handler
            // This is a safety check in case the function is called from elsewhere
            markElementAsBeingAdded(textElement.id)

            // Prepare for auto-expansion if adding an article
            prepareForAutoExpand({
                contentItemViewNodes,
                isNodeToAddArticle,
            })

            initialActiveTabRef.current =
                blurStartActiveTab ?? useWorkspace2Store.getState().activeTab
            setSaveOperationInProgress(true)

            executeWithOptimistic({
                fallbackState,
                asyncAction: () => addProjectContentItem({ variables }),
                successCbFn: async () => {
                    await handleContentMutationCompleted(
                        textElement,
                        isNodeToAddArticle,
                        true,
                        blurStartActiveTab
                    )
                    markElementAsAdded(textElement.id)
                    setSaveOperationInProgress(false)
                },
                errorCbFn: () => {
                    markElementAsAdded(textElement.id)
                    updateTreeViewNodes(
                        previousContentItemViewNodes as Partial<
                            ContentItemViewNodesType
                        >[]
                    )
                    cleanupAutoExpand()
                    resetOperationStates()
                },
            })
        },
        [
            handleContentMutationCompleted,
            executeWithOptimistic,
            isNodeToAddArticle,
            updateTreeViewNodes,
            addProjectContentItem,
            addNewArticle,
            resetOperationStates,
            markElementAsBeingAdded,
            markElementAsAdded,
            prepareForAutoExpand,
            cleanupAutoExpand,
            contentItemViewNodes,
        ]
    )

    const handleAddParagraphContentExecuteOptimisticWithFallback = useCallback(
        async (
            fallbackState: string,
            textElement: TextContentElement,
            blurStartActiveTab?: number
        ) => {
            const variables = addNewParagraph(fallbackState) || undefined

            if (!variables) {
                throw new Error(`Unsupported Type: ${nodeToAdd?.type}`)
            }

            // Element should already be marked as being added in the blur handler
            // This is a safety check in case the function is called from elsewhere
            markElementAsBeingAdded(textElement.id)

            initialActiveTabRef.current =
                blurStartActiveTab ?? useWorkspace2Store.getState().activeTab
            setSaveOperationInProgress(true)

            executeWithOptimistic({
                fallbackState,
                asyncAction: () => addProjectContentItem({ variables }),
                successCbFn: async () => {
                    await handleContentMutationCompleted(
                        textElement,
                        isNodeToAddArticle,
                        true,
                        blurStartActiveTab
                    )
                    markElementAsAdded(textElement.id)
                    setSaveOperationInProgress(false)
                },
                errorCbFn: () => {
                    markElementAsAdded(textElement.id)
                    updateTreeViewNodes(
                        previousContentItemViewNodes as Partial<
                            ContentItemViewNodesType
                        >[]
                    )
                    cleanupAutoExpand()
                    resetOperationStates()
                },
            })
        },
        [
            handleContentMutationCompleted,
            executeWithOptimistic,
            isNodeToAddArticle,
            updateTreeViewNodes,
            addProjectContentItem,
            addNewParagraph,
            resetOperationStates,
            markElementAsBeingAdded,
            markElementAsAdded,
            cleanupAutoExpand,
        ]
    )

    const handleAddContentExecuteOptimisticWithFallback = useCallback(
        async (
            fallbackState: string,
            textElement: TextContentElement,
            blurStartActiveTab?: number
        ) => {
            if (isNodeToAddArticle) {
                await handleAddArticleContentExecuteOptimisticWithFallback(
                    fallbackState,
                    textElement,
                    blurStartActiveTab
                )
            } else if (!isNodeToAddArticle) {
                await handleAddParagraphContentExecuteOptimisticWithFallback(
                    fallbackState,
                    textElement,
                    blurStartActiveTab
                )
            } else {
                throw new Error(`Unsupported Type: ${nodeToAdd?.type}`)
            }
        },
        [
            handleAddArticleContentExecuteOptimisticWithFallback,
            handleAddParagraphContentExecuteOptimisticWithFallback,
            isNodeToAddArticle,
            nodeToAdd?.type,
        ]
    )

    return {
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
    }
}
